import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getAccessToken, getUserId } from 'utils/common'

export type DispatchUser = {
  id: string
  adminId: string
  role: string
  email: string
  verified: boolean | null
  createdAt: string
  modifiedAt: string
}

export type CreateDispatchUserRequest = {
  email: string
  role?: string
}

export type UpdateDispatchUserRequest = {
  id: string
  role?: string
}

export const dispatchUsersApi = createApi({
  reducerPath: 'dispatchUsersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`,
    timeout: 2 * 60 * 1000,
    prepareHeaders: (headers) => {
      const token = getAccessToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['DispatchUsers'],
  endpoints: (builder) => ({
    getDispatchUsers: builder.query<DispatchUser[], void>({
      query: () => {
        const adminId = getUserId()
        return {
          url: `oapi/v1/users/${adminId}/dispatch-users`,
          method: 'GET',
        }
      },
      providesTags: ['DispatchUsers'],
    }),
    addDispatchUser: builder.mutation<DispatchUser, CreateDispatchUserRequest>({
      query: (body) => {
        const adminId = getUserId()
        return {
          url: `oapi/v1/users/${adminId}/dispatch-users`,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: ['DispatchUsers'],
    }),
    updateDispatchUser: builder.mutation<DispatchUser, UpdateDispatchUserRequest>({
      query: ({ id, role }) => {
        const adminId = getUserId()
        return {
          url: `oapi/v1/users/${adminId}/dispatch-users/${id}`,
          method: 'PUT',
          body: { role },
        }
      },
      invalidatesTags: ['DispatchUsers'],
    }),
    deleteDispatchUser: builder.mutation<void, string>({
      query: (id) => {
        const adminId = getUserId()
        return {
          url: `oapi/v1/users/${adminId}/dispatch-users/${id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['DispatchUsers'],
    }),
  }),
})

export const {
  useGetDispatchUsersQuery,
  useAddDispatchUserMutation,
  useUpdateDispatchUserMutation,
  useDeleteDispatchUserMutation,
} = dispatchUsersApi
